import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, ChevronLeft, ChevronRight, Search, Wallet, Clock, CheckCircle2, XCircle, TrendingDown, Banknote, Receipt, QrCode, Copy, Sparkles, ShieldCheck, ShieldAlert, ShieldX, ShieldOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import {
  useAdminApproveCpmPayoutMutation,
  useAdminCpmPayoutsQuery,
  useAdminDeleteCpmPayoutMutation,
} from '@/generated/graphql';
import { formatCents, formatDateTime } from '@/lib/format';
import { useToast } from '@/hooks/use-toast';
import { RegisterPayoutDialog } from '@/components/admin/RegisterPayoutDialog';
import { PAYOUT_FEE_PERCENT, calcPayoutFee, calcPayoutNet } from '@/lib/payout/constants';
import { splitPayoutsByStatus, summarizePendingPayouts } from '@/lib/payout/splitPayouts';
import { buildPixBrCode, normalizePixKey } from '@/lib/payout/pixBrCode';

const PAGE_SIZE = 20;

interface PayoutBankData {
  cpf?: string;
  pixKey?: string;
  bank?: string;
  agency?: string;
  account?: string;
}

// Shape of `redemption_data` on a redemption cpm-payout row (written by the
// backend's decideRedeemWalletForPlan / decideRedeemWalletForMinutes — keep in
// sync). Plan rows carry plan_* + cycle; minutes rows carry minutes +
// price_cents.
interface RedemptionData {
  plan_document_id?: string;
  plan_name?: string;
  cycle?: 'monthly' | 'yearly';
  plan_price_cents?: number;
  minutes?: number;
  price_cents?: number;
  wallet_applied_cents?: number;
  remaining_cents?: number;
  wallet_discount_percent?: number;
  wallet_discount_cents?: number;
  remainder_method?: 'credit_card' | 'pix' | null;
  remainder_order_id?: string | null;
  renewal?: boolean;
}

function redemptionProductLabel(red: RedemptionData): string {
  if (red.minutes) return `${red.minutes} minutos extras`;
  return `${red.plan_name ?? '—'} · ${red.cycle === 'yearly' ? 'Anual' : 'Mensal'}`;
}

// Many clipadores never set a display name, so the backend falls back to the
// email as `user_name` — rendering both lines then just repeats the email
// twice. Only show the second line when it's genuinely different information.
function resolveClipadorDisplay(
  name: string | null | undefined,
  email: string | null | undefined
): { primary: string; secondary: string | null } {
  const trimmedName = name?.trim() || null;
  const trimmedEmail = email?.trim() || null;
  if (!trimmedName || trimmedName === trimmedEmail) {
    return { primary: trimmedEmail || trimmedName || '—', secondary: null };
  }
  return { primary: trimmedName, secondary: trimmedEmail };
}

interface RedemptionRow {
  id: string;
  userName: string | null;
  userEmail: string | null;
  // 'paid': settled (full coverage settles instantly; mixed settles when the
  // remainder webhook confirms and the wallet is debited). 'pending': a mixed
  // redemption still awaiting its card/PIX remainder — the balance is held,
  // not yet debited.
  status: string;
  createdAt: string;
  redemption: RedemptionData;
}

const REDEMPTIONS_PAGE_SIZE = 8;

// `pix_verification_status` is resolved by the backend from the clipador's
// latest verification — a pending payout request always shows one of these
// four states, 'unverified' when they never submitted a verification at all.
const PIX_VERIFICATION_STATUS_LABEL: Record<string, string> = {
  unverified: 'Não enviado',
  pending: 'Em análise',
  verified: 'Verificado',
  rejected: 'Reprovado',
};

const PIX_VERIFICATION_ICON: Record<string, { Icon: typeof ShieldCheck; className: string }> = {
  verified: { Icon: ShieldCheck, className: 'text-emerald-500' },
  pending: { Icon: ShieldAlert, className: 'text-amber-500' },
  rejected: { Icon: ShieldX, className: 'text-red-500' },
  unverified: { Icon: ShieldOff, className: 'text-muted-foreground' },
};

function pixVerificationBadgeVariant(status: string): 'default' | 'destructive' | 'outline' {
  if (status === 'verified') return 'default';
  if (status === 'rejected') return 'destructive';
  return 'outline';
}

export default function AdminPayouts() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const userIdParam = searchParams.get('userId') || undefined;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [qrTarget, setQrTarget] = useState<string | null>(null);

  const [redemptionSearch, setRedemptionSearch] = useState('');
  const [debouncedRedemptionSearch, setDebouncedRedemptionSearch] = useState('');
  const [redemptionPage, setRedemptionPage] = useState(1);
  const [receiptTarget, setReceiptTarget] = useState<RedemptionRow | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedRedemptionSearch(redemptionSearch), 300);
    return () => clearTimeout(handle);
  }, [redemptionSearch]);

  // The PIX-withdrawal list must not show plan redemptions (they are a
  // separate history below); the backend folds legacy NULL-kind rows into
  // pix_withdrawal.
  const { data, loading, refetch } = useAdminCpmPayoutsQuery({
    variables: {
      search: debouncedSearch || undefined,
      userId: userIdParam,
      kind: 'pix_withdrawal',
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: redemptionsData, loading: redemptionsLoading } = useAdminCpmPayoutsQuery({
    variables: {
      search: debouncedRedemptionSearch || undefined,
      userId: userIdParam,
      kind: 'redemption',
      limit: REDEMPTIONS_PAGE_SIZE,
      offset: (redemptionPage - 1) * REDEMPTIONS_PAGE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [deletePayout] = useAdminDeleteCpmPayoutMutation();
  const [approvePayout, { loading: approving }] = useAdminApproveCpmPayoutMutation();

  const rows = data?.adminCpmPayouts?.data ?? [];
  const total = data?.adminCpmPayouts?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const { pending: pendingRequests, paid: paidRows } = splitPayoutsByStatus(rows);
  const pendingSummary = summarizePendingPayouts(pendingRequests);

  const pagedRedemptions: RedemptionRow[] = (redemptionsData?.adminCpmPayouts?.data ?? []).map((r) => ({
    id: r.id,
    userName: r.user_name ?? null,
    userEmail: r.user_email ?? null,
    status: r.status,
    createdAt: r.requested_at || r.createdAt,
    redemption: (r.redemption_data ?? {}) as RedemptionData,
  }));
  const redemptionTotal = redemptionsData?.adminCpmPayouts?.total ?? 0;
  const redemptionTotalPages = Math.max(1, Math.ceil(redemptionTotal / REDEMPTIONS_PAGE_SIZE));

  const handleApprovePending = async (id: string) => {
    if (approving) return;
    try {
      await approvePayout({ variables: { id } });
      toast({ title: 'Pagamento registrado com sucesso.' });
      refetch();
    } catch (err) {
      toast({
        title: t('admin.payouts.toastError'),
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deletePayout({ variables: { id: deleteId } });
      toast({ title: t('admin.payouts.toastDeleted') });
      setDeleteId(null);
      refetch();
    } catch (err) {
      toast({
        title: t('admin.payouts.toastError'),
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      });
    }
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto space-y-4">

          {/* Fee summary cards */}
          {(() => {
            const { totalGrossCents: totalGross, totalFeeCents: totalFee, totalNetCents: totalNet, currency } = pendingSummary;
            return (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Card>
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Banknote className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">Bruto pendente</p>
                      <p className="text-xl font-bold">{formatCents(totalGross, currency)}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {pendingRequests.length} solicitaç{pendingRequests.length === 1 ? 'ão' : 'ões'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-amber-500/30 bg-amber-500/5">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                      <Receipt className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-amber-400 uppercase tracking-wider font-semibold mb-0.5">Taxa retida ({PAYOUT_FEE_PERCENT}%)</p>
                      <p className="text-xl font-bold text-amber-300">{formatCents(totalFee, currency)}</p>
                      <p className="text-xs text-amber-400/70 mt-0.5 leading-relaxed">
                        Impostos · Parcerias · Infraestrutura
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-emerald-500/30 bg-emerald-500/5">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
                      <TrendingDown className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-emerald-400 uppercase tracking-wider font-semibold mb-0.5">Líquido a pagar</p>
                      <p className="text-xl font-bold text-emerald-300">{formatCents(totalNet, currency)}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Valor que sai do caixa</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Pending requests section */}
          {pendingRequests.length > 0 && (
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-amber-300 text-base">
                  <Clock className="w-4 h-4" />
                  Solicitações Pendentes
                  <span className="ml-auto text-xs font-normal bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">
                    {pendingRequests.length} pendente{pendingRequests.length !== 1 ? 's' : ''}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border border-amber-500/20 [&_th]:px-2.5 [&_th]:py-2 [&_td]:px-2.5 [&_td]:py-2">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Clipador</TableHead>
                        <TableHead>Verificação</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>PIX / Banco</TableHead>
                        <TableHead>Solicitado em</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingRequests.map((req) => {
                        const bankData = (req.bank_data ?? {}) as PayoutBankData;
                        const normalizedPixKey = req.currency === 'BRL'
                          ? normalizePixKey(bankData.pixKey ?? '')
                          : null;
                        const pixStatus = req.pix_verification_status ?? 'unverified';
                        const pixLabel = PIX_VERIFICATION_STATUS_LABEL[pixStatus] ?? pixStatus;
                        const { Icon: PixStatusIcon, className: pixIconClassName } =
                          PIX_VERIFICATION_ICON[pixStatus] ?? PIX_VERIFICATION_ICON.unverified;
                        const pixTooltip =
                          pixStatus === 'rejected' && req.pix_verification_review_reason
                            ? `${pixLabel}: ${req.pix_verification_review_reason}`
                            : `Verificação de PIX: ${pixLabel}`;
                        const clipador = resolveClipadorDisplay(req.user_name, req.user_email);
                        return (
                          <Fragment key={req.id}>
                            <TableRow>
                              <TableCell>
                                <div className="flex items-center gap-1.5">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="shrink-0 cursor-help">
                                        <PixStatusIcon className={`w-3.5 h-3.5 ${pixIconClassName}`} />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{pixTooltip}</TooltipContent>
                                  </Tooltip>
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium truncate max-w-[130px]" title={clipador.primary}>
                                      {clipador.primary}
                                    </div>
                                    {clipador.secondary && (
                                      <div
                                        className="text-xs text-muted-foreground truncate max-w-[130px]"
                                        title={clipador.secondary}
                                      >
                                        {clipador.secondary}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant={pixVerificationBadgeVariant(pixStatus)} className="text-[10px]">
                                  {pixLabel}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="font-semibold">{formatCents(req.amount_cents, req.currency)}</div>
                                <div className="text-xs text-amber-500/80 mt-0.5">
                                  − {formatCents(calcPayoutFee(req.amount_cents), req.currency)} taxa
                                </div>
                                <div className="text-xs text-emerald-400 font-medium">
                                  = {formatCents(calcPayoutNet(req.amount_cents), req.currency)} líquido
                                </div>
                              </TableCell>
                              <TableCell
                                className="text-sm text-muted-foreground max-w-[108px] truncate"
                                title={bankData.pixKey || (bankData.bank ? `${bankData.bank} • Ag ${bankData.agency} • Cc ${bankData.account}` : undefined)}
                              >
                                {bankData.pixKey || (bankData.bank ? `${bankData.bank} • Ag ${bankData.agency} • Cc ${bankData.account}` : '—')}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDateTime(req.requested_at)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex flex-wrap items-center justify-end gap-1.5 max-w-[172px] ml-auto">
                                  {normalizedPixKey && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-1.5"
                                      onClick={() => setQrTarget(qrTarget === req.id ? null : req.id)}
                                    >
                                      <QrCode className="w-3.5 h-3.5" />
                                      QR PIX
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10"
                                    onClick={() => setDeleteId(req.id)}
                                  >
                                    <XCircle className="w-3.5 h-3.5" />
                                    Recusar
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5"
                                    disabled={approving}
                                    onClick={() => handleApprovePending(req.id)}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    Marcar como pago
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                            {qrTarget === req.id && normalizedPixKey && (() => {
                              const netCents = calcPayoutNet(req.amount_cents);
                              const payload = buildPixBrCode({
                                pixKey: normalizedPixKey,
                                amountCents: netCents,
                                receiverName: req.user_name || req.user_email || '',
                              });
                              return (
                                <TableRow>
                                  <TableCell colSpan={6}>
                                    <div className="flex flex-col sm:flex-row items-start gap-4 p-2">
                                      <div className="bg-white rounded-lg p-3 shrink-0">
                                        <QRCodeSVG value={payload} size={220} marginSize={2} />
                                      </div>
                                      <div className="space-y-2 min-w-0 flex-1">
                                        <p className="text-sm text-muted-foreground">
                                          Escaneie com o app do seu banco para pagar
                                        </p>
                                        <p className="text-2xl font-bold text-emerald-400">
                                          {formatCents(netCents, 'BRL')}
                                        </p>
                                        <p className="text-sm font-medium">
                                          {req.user_name || req.user_email}
                                        </p>
                                        <p className="text-xs text-muted-foreground break-all">
                                          Chave: {bankData.pixKey}
                                        </p>
                                        {normalizedPixKey !== bankData.pixKey && (
                                          <p className="text-xs text-muted-foreground break-all">
                                            Chave codificada no QR: {normalizedPixKey}
                                          </p>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                          O app do banco mostra o nome do titular da chave antes de confirmar.
                                        </p>
                                        <div className="flex items-center gap-2">
                                          <Input
                                            readOnly
                                            value={payload}
                                            className="text-xs font-mono"
                                            onFocus={(e) => e.target.select()}
                                          />
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-1.5 shrink-0"
                                            onClick={async () => {
                                              try {
                                                await navigator.clipboard.writeText(payload);
                                                toast({ title: 'Código copiado!' });
                                              } catch {
                                                toast({ title: 'Não foi possível copiar', variant: 'destructive' });
                                              }
                                            }}
                                          >
                                            <Copy className="w-3.5 h-3.5" />
                                            Copiar
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })()}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Plan redemptions (wallet-funded subscriptions) */}
          <Card className="border-lime-500/30 bg-lime-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lime-600 dark:text-lime-400 text-base">
                <Sparkles className="w-4 h-4" />
                Resgates por assinatura
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Assinaturas e minutos extras pagos (total ou parcialmente) com o saldo da carteira CPM.
                Não aparecem na lista de pagamentos PIX abaixo — é um histórico separado.
              </p>
              <p className="text-xs text-muted-foreground">
                Resgate 100% coberto pelo saldo conclui na hora. Resgate misto (saldo + cartão/PIX) fica{' '}
                <span className="font-medium text-amber-600 dark:text-amber-400">pendente</span> até o
                pagamento do restante confirmar — só então o saldo é debitado.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Buscar por nome ou e-mail..."
                  value={redemptionSearch}
                  onChange={(e) => {
                    setRedemptionSearch(e.target.value);
                    setRedemptionPage(1);
                  }}
                />
              </div>

              {pagedRedemptions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  {redemptionsLoading ? 'Carregando…' : 'Nenhum resgate por assinatura ainda.'}
                </p>
              ) : (
                <>
                  <div className="rounded-md border border-lime-500/20">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Clipador</TableHead>
                          <TableHead>Produto</TableHead>
                          <TableHead>Saldo aplicado</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedRedemptions.map((r) => {
                          const isPending = r.status === 'pending';
                          const clipador = resolveClipadorDisplay(r.userName, r.userEmail);
                          return (
                            <TableRow key={r.id}>
                              <TableCell>
                                <div className="text-sm font-medium">{clipador.primary}</div>
                                {clipador.secondary && (
                                  <div className="text-xs text-muted-foreground">{clipador.secondary}</div>
                                )}
                              </TableCell>
                              <TableCell className="text-sm">
                                {redemptionProductLabel(r.redemption)}
                                {r.redemption.renewal && (
                                  <> · {t('admin.payouts.renewalLabel')}</>
                                )}
                              </TableCell>
                              <TableCell className="text-sm font-medium text-lime-600 dark:text-lime-400">
                                {formatCents(r.redemption.wallet_applied_cents ?? 0, 'BRL')}
                              </TableCell>
                              <TableCell>
                                {isPending ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 text-[11px] font-bold cursor-help">
                                        <Clock className="w-3 h-3" />
                                        Pendente
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Falta pagar {formatCents(r.redemption.remaining_cents ?? 0, 'BRL')} via{' '}
                                      {r.redemption.remainder_method === 'pix' ? 'PIX' : 'Cartão'}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : r.status === 'cancelled' ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-bold cursor-help">
                                        <XCircle className="w-3 h-3" />
                                        Cancelado
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Cancelado pelo usuário — saldo liberado, nada debitado. Se o restante
                                      for pago mesmo assim, vira Concluído e o saldo é debitado.
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lime-500/15 text-lime-600 dark:text-lime-400 text-[11px] font-bold">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Concluído
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDateTime(r.createdAt)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1.5"
                                  onClick={() => setReceiptTarget(r)}
                                >
                                  <Receipt className="w-3.5 h-3.5" />
                                  Ver recibo
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {redemptionTotal} total
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setRedemptionPage((p) => Math.max(1, p - 1))}
                        disabled={redemptionPage <= 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm">{redemptionPage} / {redemptionTotalPages}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setRedemptionPage((p) => Math.min(redemptionTotalPages, p + 1))}
                        disabled={redemptionPage >= redemptionTotalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Receipt dialog — same breakdown as the plan checkout preview */}
          <Dialog open={!!receiptTarget} onOpenChange={(o) => !o && setReceiptTarget(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-lime-500" />
                  Recibo do resgate
                </DialogTitle>
              </DialogHeader>
              {receiptTarget && (() => {
                const isPending = receiptTarget.status === 'pending';
                const red = receiptTarget.redemption;
                const clipador = resolveClipadorDisplay(receiptTarget.userName, receiptTarget.userEmail);
                return (
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium">{clipador.primary}</p>
                        {clipador.secondary && (
                          <p className="text-xs text-muted-foreground">{clipador.secondary}</p>
                        )}
                      </div>
                      {isPending ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 text-[11px] font-bold shrink-0">
                          <Clock className="w-3 h-3" />
                          Pendente
                        </span>
                      ) : receiptTarget.status === 'cancelled' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-bold shrink-0">
                          <XCircle className="w-3 h-3" />
                          Cancelado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lime-500/15 text-lime-600 dark:text-lime-400 text-[11px] font-bold shrink-0">
                          <CheckCircle2 className="w-3 h-3" />
                          Concluído
                        </span>
                      )}
                    </div>
                    <div className="pt-2 border-t border-dashed border-border space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Produto</span>
                        <span className="font-medium">
                          {redemptionProductLabel(red)}
                          {red.renewal && (
                            <> · {t('admin.payouts.renewalLabel')}</>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Valor</span>
                        <span>{formatCents(red.plan_price_cents ?? red.price_cents ?? 0, 'BRL')}</span>
                      </div>
                      {(red.wallet_discount_cents ?? 0) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            Desconto carteira ({red.wallet_discount_percent ?? 0}%)
                          </span>
                          <span className="font-medium text-lime-600 dark:text-lime-400">
                            − {formatCents(red.wallet_discount_cents ?? 0, 'BRL')}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Saldo aplicado</span>
                        <span className="font-medium text-lime-600 dark:text-lime-400">
                          − {formatCents(red.wallet_applied_cents ?? 0, 'BRL')}
                        </span>
                      </div>
                      {(red.remaining_cents ?? 0) > 0 && red.remainder_method && (
                        <div className="flex justify-between text-sm">
                          <span className={isPending ? 'text-amber-500' : 'text-muted-foreground'}>
                            {isPending ? 'Aguardando' : 'Restante'} ({red.remainder_method === 'pix' ? 'PIX' : 'Cartão'})
                          </span>
                          <span className={isPending ? 'font-medium text-amber-500' : ''}>
                            {formatCents(red.remaining_cents ?? 0, 'BRL')}
                          </span>
                        </div>
                      )}
                      {red.remainder_order_id && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Ordem do restante</span>
                          <span className="font-mono text-xs">{red.remainder_order_id}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground pt-1 border-t border-dashed border-border">
                      {formatDateTime(receiptTarget.createdAt)}
                    </p>
                  </div>
                );
              })()}
            </DialogContent>
          </Dialog>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5" /> {t('admin.payouts.title')}
              </CardTitle>
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-1" />
                {t('admin.payouts.registerButton')}
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder={t('admin.payouts.searchPlaceholder')}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              {paidRows.length === 0 && !loading ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  {t('admin.payouts.empty')}
                </p>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('admin.payouts.columns.user')}</TableHead>
                          <TableHead>{t('admin.payouts.columns.amount')}</TableHead>
                          <TableHead>{t('admin.payouts.columns.currency')}</TableHead>
                          <TableHead>{t('admin.payouts.columns.paidAt')}</TableHead>
                          <TableHead>{t('admin.payouts.columns.note')}</TableHead>
                          <TableHead>{t('admin.payouts.columns.createdBy')}</TableHead>
                          <TableHead className="text-right">{t('admin.payouts.columns.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paidRows.map((p) => {
                          const clipador = resolveClipadorDisplay(p.user_name, p.user_email);
                          return (
                          <TableRow key={p.id}>
                            <TableCell>
                              <div className="text-sm font-medium">{clipador.primary}</div>
                              {clipador.secondary && (
                                <div className="text-xs text-muted-foreground">{clipador.secondary}</div>
                              )}
                            </TableCell>
                            <TableCell>{formatCents(p.amount_cents, p.currency)}</TableCell>
                            <TableCell>{p.currency}</TableCell>
                            <TableCell>{formatDateTime(p.paid_at)}</TableCell>
                            <TableCell className="max-w-xs truncate" title={p.note || ''}>
                              {p.note || '—'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {p.created_by_email || '—'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteId(p.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {total} total
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm">{page} / {totalPages}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <RegisterPayoutDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          toast({ title: t('admin.payouts.toastCreated') });
          refetch();
        }}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.payouts.confirmDeleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.payouts.confirmDeleteBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin.payouts.cancelAction')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              {t('admin.payouts.deleteAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
